import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { CartStoreItem, CartTuitionItem } from '@/features/payments/types';
import { storeCartKey, tuitionCartKey } from '@/features/payments/paymentUtils';

interface PaymentsState {
  tuitionItems: CartTuitionItem[];
  storeItems: CartStoreItem[];
  customAmounts: Record<string, string>;
  excludedTuitionKeys: string[];
  setCustomAmount: (programId: string, feeId: string, value: string) => void;
  clearCustomAmounts: (programId: string, feeId?: string) => void;
  addTuitionItem: (item: Omit<CartTuitionItem, 'id'>) => void;
  removeTuitionItem: (programId: string, feeId: string) => void;
  setTuitionIncluded: (programId: string, feeId: string, included: boolean) => void;
  isTuitionInCart: (programId: string, feeId: string) => boolean;
  isTuitionIncluded: (programId: string, feeId: string) => boolean;
  addStoreItem: (item: Omit<CartStoreItem, 'id'>) => void;
  addStoreItemsForChildren: (
    item: Omit<CartStoreItem, 'id' | 'childId' | 'childName'>,
    children: readonly { readonly id: string; readonly name: string }[],
  ) => void;
  removeStoreItem: (id: string) => void;
  updateStoreQuantity: (id: string, quantity: number) => void;
  updateStoreItemChild: (id: string, childId: string, childName: string) => void;
  clearCart: () => void;
}

function mergeStoreItem(
  items: readonly CartStoreItem[],
  incoming: Omit<CartStoreItem, 'id'>,
): CartStoreItem[] {
  const id = storeCartKey(incoming.productId, incoming.childId, incoming.options);
  const existing = items.find((entry) => entry.id === id);
  if (existing) {
    return items.map((entry) =>
      entry.id === id ? { ...entry, quantity: entry.quantity + (incoming.quantity || 1) } : entry,
    );
  }
  return [...items, { ...incoming, id, quantity: incoming.quantity || 1 }];
}

export const usePaymentsStore = create<PaymentsState>()(
  persist(
    (set, get) => ({
      tuitionItems: [],
      storeItems: [],
      customAmounts: {},
      excludedTuitionKeys: [],

      setCustomAmount: (programId, feeId, value) => {
        const key = tuitionCartKey(programId, feeId);
        set((state) => ({
          customAmounts: { ...state.customAmounts, [key]: value },
        }));
      },

      clearCustomAmounts: (programId, feeId) => {
        const keyPrefix = `${programId}:`;
        const targetKey = feeId ? tuitionCartKey(programId, feeId) : null;
        set((state) => ({
          customAmounts: Object.fromEntries(
            Object.entries(state.customAmounts).filter(([key]) =>
              targetKey ? key !== targetKey : !key.startsWith(keyPrefix),
            ),
          ),
        }));
      },

      addTuitionItem: (item) => {
        const key = tuitionCartKey(item.programId, item.feeId);
        set((state) => {
          const without = state.tuitionItems.filter(
            (entry) => tuitionCartKey(entry.programId, entry.feeId) !== key,
          );
          return {
            tuitionItems: [...without, { ...item, id: key }],
            excludedTuitionKeys: state.excludedTuitionKeys.filter((entry) => entry !== key),
          };
        });
      },

      removeTuitionItem: (programId, feeId) => {
        const key = tuitionCartKey(programId, feeId);
        set((state) => ({
          tuitionItems: state.tuitionItems.filter((entry) => entry.id !== key),
          excludedTuitionKeys: state.excludedTuitionKeys.filter((entry) => entry !== key),
        }));
      },

      setTuitionIncluded: (programId, feeId, included) => {
        const key = tuitionCartKey(programId, feeId);
        set((state) => {
          if (!state.tuitionItems.some((entry) => entry.id === key)) return state;
          const without = state.excludedTuitionKeys.filter((entry) => entry !== key);
          return {
            excludedTuitionKeys: included ? without : [...without, key],
          };
        });
      },

      isTuitionIncluded: (programId, feeId) => {
        const key = tuitionCartKey(programId, feeId);
        return (
          get().tuitionItems.some((entry) => entry.id === key) &&
          !get().excludedTuitionKeys.includes(key)
        );
      },

      isTuitionInCart: (programId, feeId) => {
        const key = tuitionCartKey(programId, feeId);
        return get().tuitionItems.some((entry) => entry.id === key);
      },

      addStoreItem: (item) => {
        set((state) => ({
          storeItems: mergeStoreItem(state.storeItems, item),
        }));
      },

      addStoreItemsForChildren: (item, children) => {
        set((state) => {
          let nextItems = state.storeItems;
          for (const child of children) {
            nextItems = mergeStoreItem(nextItems, {
              ...item,
              childId: child.id,
              childName: child.name,
            });
          }
          return { storeItems: nextItems };
        });
      },

      removeStoreItem: (id) => {
        set((state) => ({
          storeItems: state.storeItems.filter((entry) => entry.id !== id),
        }));
      },

      updateStoreQuantity: (id, quantity) => {
        set((state) => ({
          storeItems: state.storeItems.map((entry) =>
            entry.id === id ? { ...entry, quantity: Math.max(1, quantity) } : entry,
          ),
        }));
      },

      updateStoreItemChild: (id, childId, childName) => {
        set((state) => {
          const item = state.storeItems.find((entry) => entry.id === id);
          if (!item || item.childId === childId) return state;

          const nextKey = storeCartKey(item.productId, childId, item.options);
          const conflict = state.storeItems.find(
            (entry) => entry.id !== id && entry.id === nextKey,
          );

          if (conflict) {
            return {
              storeItems: state.storeItems
                .filter((entry) => entry.id !== id)
                .map((entry) =>
                  entry.id === conflict.id
                    ? { ...entry, quantity: entry.quantity + item.quantity }
                    : entry,
                ),
            };
          }

          const without = state.storeItems.filter((entry) => entry.id !== id);
          return {
            storeItems: mergeStoreItem(without, {
              ...item,
              childId,
              childName,
              quantity: item.quantity,
            }),
          };
        });
      },

      clearCart: () =>
        set({ tuitionItems: [], storeItems: [], customAmounts: {}, excludedTuitionKeys: [] }),
    }),
    {
      name: 'myu-payments-cart',
      version: 2,
      migrate: (persisted, version) => {
        const state = persisted as PaymentsState;
        const base = {
          ...state,
          storeItems: (state.storeItems ?? []).filter((entry) =>
            Boolean(entry.childId && entry.childName),
          ),
        };
        if (version < 2) {
          return { ...base, excludedTuitionKeys: [] };
        }
        return base;
      },
    },
  ),
);
